"use client";

import {
  Alert,
  Description,
  Input,
  Label,
  Separator,
  TextField,
} from "@heroui/react";
import { useEffect, useState } from "react";
import NextLink from "next/link";

import { GoogleButton } from "@/components/auth/google-button";
import { SubmitButton } from "@/components/auth/submit-button";
import { signUp } from "@/lib/auth-client";
import { getCaptchaConfig, getCaptchaToken } from "@/lib/recaptcha";

const MIN_PASSWORD_LENGTH = 10;

export function SignupForm({ next }: { next?: string }) {
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [captchaEnabled, setCaptchaEnabled] = useState(false);

  // The disclosure is only honest when reCAPTCHA is actually in play, and
  // whether it is comes from the server now rather than a build-time constant.
  useEffect(() => {
    let live = true;

    getCaptchaConfig().then((config) => {
      if (live) setCaptchaEnabled(Boolean(config.siteKey));
    });

    return () => {
      live = false;
    };
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsPending(true);

    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "");
    const password = String(form.get("password") ?? "");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      setIsPending(false);
      return;
    }

    // Minted at submit, not on page load: the reCAPTCHA script profiles the
    // whole session, so loading it earlier would report every visitor rather
    // than only people creating an account.
    let captchaToken: string | null = null;

    try {
      captchaToken = await getCaptchaToken("signup");
    } catch {
      setError("Could not reach the spam check. Check your connection and retry.");
      setIsPending(false);
      return;
    }

    const { error: signUpError } = await signUp.email({
      name: String(form.get("name") ?? "").trim(),
      email,
      password,
      callbackURL: next || "/dashboard",
      fetchOptions: captchaToken
        ? { headers: { "x-captcha-response": captchaToken } }
        : undefined,
    });

    if (signUpError) {
      setError(signUpError.message ?? "Could not create your account.");
      setIsPending(false);
      return;
    }

    // No auto sign-in: the account exists but cannot be used until the address
    // is verified, so the honest next step is "go check your email".
    setSentTo(email);
    setIsPending(false);
  }

  if (sentTo) {
    return (
      <Alert status="success">
        <Alert.Content>
          <Alert.Title>Check your inbox</Alert.Title>
          <Alert.Description>
            We sent a verification link to <strong>{sentTo}</strong>. Open it to
            activate your account. The link expires in an hour.
          </Alert.Description>
        </Alert.Content>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <GoogleButton next={next} />

      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-muted text-xs">or</span>
        <Separator className="flex-1" />
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error ? (
          <Alert status="danger">
            <Alert.Content>
              <Alert.Description>{error}</Alert.Description>
            </Alert.Content>
          </Alert>
        ) : null}

        <TextField name="name" isRequired autoComplete="name">
          <Label>Name</Label>
          <Input placeholder="Ada Lovelace" />
        </TextField>

        <TextField name="email" type="email" isRequired autoComplete="email">
          <Label>Email</Label>
          <Input placeholder="you@company.com" />
        </TextField>

        <TextField
          name="password"
          type="password"
          isRequired
          autoComplete="new-password"
        >
          <Label>Password</Label>
          <Input placeholder="••••••••••" />
          <Description>At least {MIN_PASSWORD_LENGTH} characters.</Description>
        </TextField>

        <SubmitButton isPending={isPending}>Create account</SubmitButton>

        {/* DPDP s.5: the notice has to be given on or before consent is
            sought, so it sits with the button that creates the account rather
            than in a footer link somebody may never scroll to. The itemised
            version is one click away and the age statement is here because
            s.9 makes under-18 accounts a different legal proposition, not a
            checkbox. */}
        <p className="text-muted text-center text-xs leading-relaxed">
          By creating an account you confirm you are 18 or over and have read
          our{" "}
          <NextLink href="/legal/privacy" className="underline underline-offset-4">
            privacy notice
          </NextLink>
          , which sets out what we collect, why, and how to have it deleted.
        </p>

        {/* Required by Google's terms when the v3 badge is hidden, which it is
            — v3 has no widget, so without this nothing tells someone their
            behaviour is being scored by a third party. */}
        {captchaEnabled ? (
          <p className="text-muted text-center text-xs leading-relaxed">
            Protected by reCAPTCHA. Google&apos;s{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4"
            >
              privacy policy
            </a>{" "}
            and{" "}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-4"
            >
              terms
            </a>{" "}
            apply.
          </p>
        ) : null}
      </form>
    </div>
  );
}
